import { DOCUMENT, Location } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatTabGroup } from '@angular/material/tabs';
import Env from '@ridango/core/env';
import {
    ApplicationFacade,
    FavoritesFacade,
    FavoriteTicket,
    PurchaseService,
    ShoppingCartService
} from '@ridango/components';
import { BannersFacade } from '@ridango/components/lib/banners';
import { AllowedPathService, AuthService } from '@ridango/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DeviceIdService, PullToRefreshService } from '@ridango/core/lib/services';
import { NetworkService } from '@ridango/core';
import { PullToRefreshPages } from '@ridango/core/lib/services/pull-to-refresh.service';

import { debounceTime, filter, map, switchMap, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { AnonymousUserModalComponent } from '@ridango/widgets';
import { ScreenOrientationService } from '@ridango/core/lib/services/screenOrientation.service';
import { AnalyticsService } from '@ridango/core/lib/services/analytics.service';
import { TrackedEventCategory } from '@ridango/core/lib/enums/analytics/tracked-event-categories.enum';
import { TrackedEventAction } from '@ridango/core/lib/enums/analytics/tracked-event-action.enum';

@Component({
    templateUrl: './tickets-page.component.html',
    styleUrls: ['./tickets-page.component.scss']
})
export class TicketsPageComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('tabGroup') tabGroup: MatTabGroup;
    @ViewChild('bannersContainer') bannersContainer?: ElementRef<HTMLElement>;
    private readonly TAB_TITLES = ['Valid', 'Favorites', 'Expired'];
    private readonly TAB = 'Tab';
    private readonly BUY_TICKET = 'Buy ticket';

    public currentTabIndex = 0;
    private componentDestroyed$ = new Subject<void>();
    public assetsPath$ = this.applicationFacade.assetsPath$;
    public favoriteTickets: FavoriteTicket[] = [];
    public isWebPurchaseAllowed = this.pathService.isWebPurchaseAllowed();
    public isOnline$ = this.networkService.getStatus();

    // Disable pagination arrows for bigger than smallest screens that can fit tabs
    public disablePagination = false;
    private timeoutId = null;

    public isInLandscapeMode = this.screenOrientationService.isInLandscapeMode;
    public wasOnFavoritesTab = false;
    public banners$ = this.bannersFacade.banners$.pipe(map((banners) => banners.slice(0, 3)));

    constructor(
        private route: ActivatedRoute,
        private _location: Location,
        private applicationFacade: ApplicationFacade,
        private bannersFacade: BannersFacade,
        private purchaseService: PurchaseService,
        private authService: AuthService,
        private router: Router,
        private translate: TranslateService,
        private pullToRefresh: PullToRefreshService,
        @Inject('ENV') public env: Env,
        private deviceIdService: DeviceIdService,
        private favoritesFacade: FavoritesFacade,
        private pathService: AllowedPathService,
        private networkService: NetworkService,
        private shoppingCartService: ShoppingCartService,
        private screenOrientationService: ScreenOrientationService,
        private analyticsService: AnalyticsService,
        @Inject(DOCUMENT) private document: Document
    ) {}

    ngOnInit(): void {
        this.purchaseService.resetProductsLocalStorage();
        const isHistoryQuery = this.route.snapshot.queryParams['isHistory'];
        const isAuthenticated = this.authService.getLoginStatus();

        // It assumes that there's 2 tabs: Valid Tickets and Expired Tickets.
        // 3 tab logic is handled in the favoritesFacade subscription below.
        if (isHistoryQuery) {
            this.currentTabIndex = 1;
            this.clearNavigate();
        }

        if (this.env.REFRESH_TICKET_PAGE) {
            this.pullToRefresh.enablePullToRefresh(PullToRefreshPages.TicketList);
        }

        if (this.env.SHOW_FAVORITE_TICKETS && isAuthenticated) {
            this.favoritesFacade.getFavoriteTickets();

            this.favoritesFacade.favoriteTicketsLoading$
                .pipe(
                    filter((v) => !v),
                    switchMap(() => this.favoritesFacade.favoriteTickets$),
                    takeUntil(this.componentDestroyed$)
                )
                .subscribe((tickets) => {
                    const hadFavoritesTab = this.env.SHOW_FAVORITE_TICKETS && !!this.favoriteTickets?.length;
                    this.favoriteTickets = tickets;
                    const hasFavoritesTab = this.env.SHOW_FAVORITE_TICKETS && !!this.favoriteTickets?.length;

                    // Only redirect to Valid if the Favorites tab just disappeared and user was on it
                    if (
                        !hasFavoritesTab &&
                        hadFavoritesTab &&
                        this.env.SHOW_FAVORITE_TICKETS &&
                        this.wasOnFavoritesTab &&
                        !isHistoryQuery
                    ) {
                        this.currentTabIndex = 0;
                    }
                });
        }
        if (this.env.SHOW_BANNERS) {
            this.bannersFacade.getBanners();

            this.translate.onLangChange.pipe(takeUntil(this.componentDestroyed$)).subscribe(() => {
                this.bannersFacade.getBanners();
            });
        }

        this.onSelectedIndexChange();
    }

    ngAfterViewInit(): void {
        this.screenOrientationService.onlyPortraitMode$.pipe(takeUntil(this.componentDestroyed$)).subscribe(() => {
            if (this.timeoutId) {
                clearTimeout(this.timeoutId);
            }
            // So it wouldn't show pagination arrows instantly
            // Works fine with 1st tab selected, for the rest material has bug to show pagination arrows.
            this.disablePagination = true;
            this.updateTabPaginationAfterLayout();
        });

        this.syncBannersOffset();
    }

    ngOnDestroy(): void {
        this.document.documentElement.style.removeProperty('--app-banners-height');
        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();
    }

    private syncBannersOffset(): void {
        if (!this.env.SHOW_BANNERS) {
            return;
        }

        const updateOffset = (): void => {
            const height = this.bannersContainer?.nativeElement?.offsetHeight ?? 0;
            if (height > 0) {
                this.document.documentElement.style.setProperty('--app-banners-height', `${height}px`);
            } else {
                this.document.documentElement.style.removeProperty('--app-banners-height');
            }
        };

        this.banners$.pipe(debounceTime(50), takeUntil(this.componentDestroyed$)).subscribe(() => {
            setTimeout(updateOffset);
        });

        setTimeout(updateOffset);
    }

    private updateTabPaginationAfterLayout(): void {
        this.timeoutId = setTimeout(() => {
            const tabHeader = (this.tabGroup as any)?._tabHeader;

            if (tabHeader) {
                tabHeader.updatePagination();
                this.disablePagination = false;
            }
        }, 2000);
    }

    public navigateBack() {
        this._location.back();
    }

    clearNavigate() {
        this.router.navigate([], {
            queryParams: { isHistory: null },
            queryParamsHandling: 'merge'
        });
    }

    purchaseTicket() {
        this.analyticsService.eventTrack(TrackedEventAction.CLICKED, {
            category: TrackedEventCategory.TICKETS,
            name: this.BUY_TICKET
        });

        this.deviceIdService
            .getIsUserExist()
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((deviceUserExist) => {
                const routeParams: string[] = this.env.ENABLE_PURCHASE_V2 ? ['purchase-v2'] : ['purchase/ticket'];
                this.shoppingCartService.startToBuyTicket(deviceUserExist, routeParams, AnonymousUserModalComponent);
            });
    }

    getTabsCount(): number {
        if (this.env.SHOW_TICKET_HISTORY && this.env.SHOW_FAVORITE_TICKETS && this.favoriteTickets?.length) {
            return 3;
        } else if (this.env.SHOW_TICKET_HISTORY || (this.env.SHOW_FAVORITE_TICKETS && this.favoriteTickets?.length)) {
            return 2;
        }

        return 1;
    }

    getAriaLabel(tabIndex, tabLabel) {
        const label = this.translate.instant('TAB_AREA_LABEL');

        return label
            .replace('{0}', tabLabel)
            .replace('{1}', tabIndex + 1)
            .replace('{2}', this.getTabsCount());
    }

    isFavouritesTab() {
        return this.getTabsCount() > 2 && this.currentTabIndex === 1;
    }

    onSelectedIndexChange() {
        const hasFavoritesTab = this.env.SHOW_FAVORITE_TICKETS && !!this.favoriteTickets?.length;
        this.wasOnFavoritesTab = hasFavoritesTab && this.currentTabIndex === 1;
        this.tabActivityAnalyticsFootprint();
    }

    private tabActivityAnalyticsFootprint(): void {
        let activeTabTitle: string;

        if (this.currentTabIndex === 0) {
            activeTabTitle = this.TAB_TITLES[0];
        } else if (this.isFavouritesTab()) {
            activeTabTitle = this.TAB_TITLES[1];
        } else activeTabTitle = this.TAB_TITLES[2];

        this.analyticsService.eventTrack(TrackedEventAction.SELECTED, {
            category: TrackedEventCategory.TICKETS,
            label: `${this.TAB} - ${activeTabTitle}`
        });
    }
}
